import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { __ } from '../functions/commom';
import { useAppState } from '../state/StateContext';
import InputBox from './base/InputBox';
import DateSelector from './base/DateSelector';
import { ReactComponent as AddIcon } from '../assets/icons/add.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete.svg';
import { ReactComponent as LeftIcon } from '../assets/icons/left.svg';
import { ReactComponent as RightIcon } from '../assets/icons/right.svg';
import SelectorBox from './base/SelectorBox';
import Button from './base/Button';
import { DATE_FORMAT, IDENTITY_TYPE, AGE_REQUIREMENTS } from '../config';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import Box from './Box';

const useStyles = createUseStyles({
  formHeader: {
    borderBottom: '1px solid #ebebeb',
    paddingBottom: '30px',
    marginBottom: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    marginTop: '20px',
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    borderLeft: '5px solid var(--dai-primary-color)',
    paddingLeft: '10px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    borderLeft: '5px solid var(--dai-primary-color)',
    paddingLeft: '10px',
    marginBottom: '0px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    paddingBottom: '20px',
    marginBottom: '20px',
    borderBottom: '1px solid #ebebeb',
    flexWrap: 'wrap',
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  insuredPersonSet: {
    marginBottom: '20px',
  },
  deleteButton: {
    cursor: 'pointer',
    marginLeft: '10px',
    '& svg': {
      display: 'block',
      fill: '#cccccc',
      width: '20px',
      height: '20px',
    },
    '&:hover svg': {
      fill: '#999999',
    },
  },
  errorMessage: {
    color: 'red',
    fontSize: '14px',
    marginTop: '5px',
  },
  errorSummary: {
    color: 'red',
    padding: '10px',
    marginBottom: '30px',
    border: '1px solid red',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 0, 0, 0.05)',
  },
  selfNote: {
    color: 'var(--dai-primary-color)',
    fontSize: '14px',
    marginTop: '0px',
    fontStyle: 'italic',
    marginBottom: '5px',
  },
});

// Define a type for insured person
type InsuredPerson = {
  name: string | null;
  id: string | null;
  idType: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  relationship: string | null;
};

// Define a type for validation errors
type ValidationErrors = {
  [key: string]: string | undefined;
};

const InsuranceFormStep2: React.FC = () => {
  const id = useParams<{ id?: string }>().id;
  const [searchParams] = useSearchParams();

  const classes = useStyles();
  const state = useAppState();

  const coverageType = searchParams?.get('coverageType');

  // Get the selected insurance from the state
  const insuranceData = state.insurancePlans.get();
  const selectedInsurance = insuranceData?.find(
    (insurance) => insurance.id === id
  );
  const insuranceSettings = selectedInsurance?.settings;

  const maxInsuredPerson =
    selectedInsurance?.types?.includes('family') &&
    coverageType === 'individual'
      ? 1
      : insuranceSettings?.maxInsuredPerson || 9;

  // Use local state to manage insured persons
  const [localInsuredPersons, setLocalInsuredPersons] = useState<
    InsuredPerson[]
  >([]);

  // State for validation errors
  const [errors, setErrors] = useState<ValidationErrors[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Initialize local state from global state
  useEffect(() => {
    const globalInsuredPersons = state.form.insuredPersons.get();

    // If there are no insured persons in the global state, initialize with at least one
    if (!globalInsuredPersons || globalInsuredPersons.length === 0) {
      // Start with one empty insured person; relationship is chosen via placeholder
      const firstPerson = createEmptyInsuredPerson();
      setLocalInsuredPersons([firstPerson]);
      setErrors([createEmptyErrors()]);
      state.form.insuredPersons.set([firstPerson]);
    } else {
      // Make sure all insured persons have all required properties
      const validInsuredPersons = globalInsuredPersons.map((person) => {
        return {
          name: person?.name || null,
          id: person?.id || null,
          idType: person?.idType || null,
          dateOfBirth: person?.dateOfBirth
            ? moment(person?.dateOfBirth).format(DATE_FORMAT)
            : null,
          gender: person?.gender || null,
          relationship: person?.relationship || null,
        };
      });
      setLocalInsuredPersons(validInsuredPersons);

      // Initialize errors array with empty errors for each person
      setErrors(validInsuredPersons.map(() => createEmptyErrors()));
    }
  }, [state.form.insuredPersons]);

  // Function to create an empty insured person with all required properties
  const createEmptyInsuredPerson = (): InsuredPerson => {
    return {
      name: null,
      id: null,
      idType: null,
      dateOfBirth: null,
      gender: null,
      relationship: null,
    };
  };

  // Function to create empty errors object
  const createEmptyErrors = (): ValidationErrors => {
    return {
      name: undefined,
      id: undefined,
      idType: undefined,
      dateOfBirth: undefined,
      gender: undefined,
      relationship: undefined,
    };
  };

  // Add a new insured person
  const handleAddInsuredPersonField = () => {
    if (localInsuredPersons.length >= maxInsuredPerson) {
      return;
    }

    setLocalInsuredPersons((prev) => [...prev, createEmptyInsuredPerson()]);
    setErrors((prev) => [...prev, createEmptyErrors()]);
  };

  // Copy policy holder information to insured person
  const copyPolicyHolderInfo = (index: number): void => {
    setLocalInsuredPersons((prev) => {
      const newList = [...prev];

      // Get policy holder information from state
      const name = state.form.name.get();
      const gender = state.form.gender.get();
      const idType = state.form.idType.get();
      const id = state.form.id.get();
      const dateOfBirth = state.form.dateOfBirth.get();

      // Update the specific insured person with policy holder info
      newList[index] = {
        ...newList[index],
        name: name || null,
        gender: gender || null,
        idType: idType || null,
        id: id || null,
        dateOfBirth: dateOfBirth || null,
        relationship: 'self',
      };

      // Immediately update global state as well
      state.form.insuredPersons.set(newList);

      return newList;
    });

    // Clear any errors for this person
    setErrors((prev) => {
      const newErrors = [...prev];
      newErrors[index] = createEmptyErrors();
      return newErrors;
    });
  };

  // Update a field for an insured person
  const handleUpdateInsuredPersonField = (
    index: number,
    field: string,
    value: any
  ) => {
    // Special handling for relationship field
    if (field === 'relationship') {
      // If changing to 'self'
      if (value === 'self') {
        // Check if another person already has 'self' relationship
        const selfIndex = localInsuredPersons.findIndex(
          (p) => p.relationship === 'self'
        );
        if (selfIndex !== -1 && selfIndex !== index) {
          // Show confirmation dialog
          if (
            window.confirm(
              __(
                'Another insured person is already set as "Self". Do you want to change it?'
              )
            )
          ) {
            // Update the previous 'self' person to have no relationship
            setLocalInsuredPersons((prev) => {
              const newList = [...prev];
              newList[selfIndex] = {
                ...newList[selfIndex],
                relationship: null,
              };
              return newList;
            });
          } else {
            // User cancelled, don't change the relationship
            return;
          }
        }

        // Copy policy holder information to this insured person
        copyPolicyHolderInfo(index);
        return;
      }

      // Switching away from 'self': clear the copied data so the user re-enters it
      if (localInsuredPersons[index]?.relationship === 'self') {
        setLocalInsuredPersons((prev) => {
          const newList = [...prev];
          newList[index] = {
            ...newList[index],
            name: null,
            id: null,
            idType: null,
            dateOfBirth: null,
            gender: null,
            relationship: value,
          };
          state.form.insuredPersons.set(newList);
          return newList;
        });
        setErrors((prev) => {
          const newErrors = [...prev];
          newErrors[index] = createEmptyErrors();
          return newErrors;
        });
        return;
      }
    }

    // For all other fields or relationship not set to 'self'
    setLocalInsuredPersons((prev) => {
      const newList = [...prev];

      // Ensure the insured person exists
      if (!newList[index]) {
        newList[index] = createEmptyInsuredPerson();
      }

      // Check if this person has relationship 'self' and trying to change a locked field
      if (newList[index].relationship === 'self' && field !== 'relationship') {
        // Don't allow changes to fields other than relationship for 'self'
        return prev;
      }

      // Update the specific field
      newList[index] = {
        ...newList[index],
        [field]: value,
      };

      // Immediately update global state as well
      state.form.insuredPersons.set(newList);

      return newList;
    });

    // Clear the error for this field when it's updated
    setErrors((prev) => {
      const newErrors = [...prev];
      if (!newErrors[index]) {
        newErrors[index] = createEmptyErrors();
      }
      newErrors[index] = {
        ...newErrors[index],
        [field]: undefined,
      };
      return newErrors;
    });
  };

  // Delete an insured person
  const handleDeleteInsuredPersonSet = (index: number) => {
    // confirm
    if (
      !window.confirm(__('Are you sure to delete? The action is irreversible.'))
    ) {
      return;
    }

    // Update local state
    setLocalInsuredPersons((prev) => {
      const newList = [...prev];
      newList.splice(index, 1);

      // Immediately update global state as well
      state.form.insuredPersons.set(newList);

      return newList;
    });

    // Also update errors state
    setErrors((prev) => {
      const newErrors = [...prev];
      newErrors.splice(index, 1);
      return newErrors;
    });
  };

  // Calculate age in months from a Date of Birth
  const calculateAgeInMonths = (birthDate: string): number => {
    const today = moment();
    const birth = moment(birthDate);

    return (today.year() - birth.year()) * 12 + (today.month() - birth.month());
  };

  // Validate a single field
  const validateField = (
    person: InsuredPerson,
    field: string
  ): string | undefined => {
    // Get required fields from insurance settings
    const requiredInsuredFields = insuranceSettings?.requiredInsuredFields || [
      'name',
      'id',
      'idType',
      'dateOfBirth',
      'gender',
      'relationship',
    ];

    // Check if field is required
    const isRequired = requiredInsuredFields.includes(field);
    if (!isRequired) {
      return undefined;
    }

    // Get the value
    const value = person[field as keyof InsuredPerson];

    // Check if value is empty
    if (value === null || value === undefined || value === '') {
      return __('This field is required');
    }

    // Additional validations based on field type
    switch (field) {
      case 'id':
        // ID validation logic could be added here
        if (typeof value === 'string' && value.length < 3) {
          return __('ID must be at least 3 characters');
        }
        break;
      case 'dateOfBirth':
        // Date validation
        if (value) {
          const birthDate = moment(value);
          const today = moment();

          // Check if birthdate is in the future
          if (birthDate.isAfter(today)) {
            return __('Date of Birth cannot be in the future');
          }

          // Calculate age in days for relationship-based validation
          const ageInDays = today.diff(birthDate, 'days');
          const relationship = person.relationship?.toLowerCase();

          // Age validation based on relationship
          if (relationship === 'child') {
            // Child: must be 180 days to 23 years old
            const minChildBirthDate = moment().subtract(AGE_REQUIREMENTS.child.maxAgeYears, 'years');
            
            if (ageInDays < AGE_REQUIREMENTS.child.minAgeDays) {
              return __(
                'Child must be at least $1 old',
                __(AGE_REQUIREMENTS.child.minAgeDisplayKey)
              );
            }
            if (birthDate.isBefore(minChildBirthDate)) {
              return __(
                'Child must be under $1 old',
                __(AGE_REQUIREMENTS.child.maxAgeDisplayKey)
              );
            }
          } else if (relationship) {
            // Adult (self, spouse, parent): must be 18 years or older
            const minAdultBirthDate = moment().subtract(AGE_REQUIREMENTS.adult.minAgeYears, 'years');
            
            if (birthDate.isAfter(minAdultBirthDate)) {
              return __(
                'Must be at least $1 old',
                __(AGE_REQUIREMENTS.adult.minAgeDisplayKey)
              );
            }
          }

          // Also check legacy age limits if specified in settings (in months)
          const ageInMonths = calculateAgeInMonths(value);

          if (
            insuranceSettings?.minAgeInMonths &&
            insuranceSettings.minAgeInMonths > 0
          ) {
            if (ageInMonths < insuranceSettings.minAgeInMonths) {
              // Convert months to years and months for display
              const years = Math.floor(insuranceSettings.minAgeInMonths / 12);
              const months = insuranceSettings.minAgeInMonths % 12;
              let ageMessage = '';

              if (years > 0 && months > 0) {
                ageMessage = `${years} ${
                  years === 1 ? __('year') : __('years')
                } and ${months} ${months === 1 ? __('month') : __('months')}`;
              } else if (years > 0) {
                ageMessage = `${years} ${
                  years === 1 ? __('year') : __('years')
                }`;
              } else {
                ageMessage = `${months} ${
                  months === 1 ? __('month') : __('months')
                }`;
              }

              return __(
                'Age is below the minimum required age of $1',
                ageMessage
              );
            }
          }

          if (
            insuranceSettings?.maxAgeInMonths &&
            insuranceSettings.maxAgeInMonths > 0
          ) {
            if (ageInMonths > insuranceSettings.maxAgeInMonths) {
              // Convert months to years and months for display
              const years = Math.floor(insuranceSettings.maxAgeInMonths / 12);
              const months = insuranceSettings.maxAgeInMonths % 12;
              let ageMessage = '';

              if (years > 0 && months > 0) {
                ageMessage = `${years} ${
                  years === 1 ? __('year') : __('years')
                } and ${months} ${months === 1 ? __('month') : __('months')}`;
              } else if (years > 0) {
                ageMessage = `${years} ${
                  years === 1 ? __('year') : __('years')
                }`;
              } else {
                ageMessage = `${months} ${
                  months === 1 ? __('month') : __('months')
                }`;
              }

              return __(
                'Age exceeds the maximum allowed age of $1',
                ageMessage
              );
            }
          }
        }
        break;
    }

    return undefined;
  };

  // Validate all fields for a person
  const validatePerson = (person: InsuredPerson, index: number): boolean => {
    const newErrors: ValidationErrors = createEmptyErrors();
    let hasErrors = false;

    // Get all fields to validate
    const fieldsToValidate = Object.keys(person);

    // Validate each field
    fieldsToValidate.forEach((field) => {
      const error = validateField(person, field);
      newErrors[field] = error;
      if (error) {
        hasErrors = true;
      }
    });

    // Update errors state for this person
    setErrors((prev) => {
      const newErrorsArray = [...prev];
      newErrorsArray[index] = newErrors;
      return newErrorsArray;
    });

    return !hasErrors;
  };

  // Validate all insured persons
  const handleStep2Validation = (): boolean => {
    // Reset form error
    setFormError(null);

    // Check if we have at least one insured person
    if (localInsuredPersons.length === 0) {
      console.log(localInsuredPersons);
      setFormError(__('At least one insured person is required'));
      return false;
    }

    // Check if we exceed maximum number of insured persons
    if (
      localInsuredPersons.length >
      (coverageType === 'family' ? maxInsuredPerson : 1)
    ) {
      setFormError(
        __(
          'The maximum number of insured persons has been exceeded. Please delete $1 insured person(s) before proceeding.',
          localInsuredPersons.length -
            (coverageType === 'family' ? maxInsuredPerson : 1)
        )
      );
      return false;
    }

    // Check minimum insured persons for family plan
    if (coverageType === 'family' && localInsuredPersons.length < 2) {
      setFormError(
        __(
          'Family plan requires at least 2 insured persons. Please add more insured persons.'
        )
      );
      return false;
    }

    // Validate each person
    let isValid = true;
    localInsuredPersons.forEach((person, index) => {
      const personValid = validatePerson(person, index);
      if (!personValid) {
        isValid = false;
      }
    });

    return isValid;
  };

  // Save to global state and go to next step
  const handleNext = () => {
    // Validate all fields before proceeding
    const isValid = handleStep2Validation();

    if (isValid) {
      // Update the global state with our local state
      state.form.insuredPersons.set(localInsuredPersons);
      state.formStep.set(3);
    } else {
      // Scroll to the top to show error messages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Save to global state and go to previous step
  const handleBack = () => {
    state.formStep.set(1);
  };

  const FormFooter: React.FC = () => {
    return (
      <div className={classes.formFooter}>
        <Button onClick={handleBack} icon={<LeftIcon />} iconPosition="before">
          {__('Back')}
        </Button>
        <Button
          theme="primary"
          onClick={handleNext}
          icon={<RightIcon />}
          iconPosition="after"
        >
          {__('Next')}
        </Button>
      </div>
    );
  };

  // Check if a field should be disabled (locked) for a specific person
  const isFieldDisabled = (person: InsuredPerson, field: string): boolean => {
    return person.relationship === 'self' && field !== 'relationship';
  };

  // Get relationship options for a specific insured person
  const getRelationshipOptions = (index: number) => {
    // Find if there's already a 'self' relationship
    const selfIndex = localInsuredPersons.findIndex(
      (p) => p.relationship === 'self'
    );

    // Family plan only covers spouse & dependent children, not parent
    const baseOptions =
      coverageType === 'family'
        ? [
            { value: 'spouse', label: __('Spouse') },
            { value: 'child', label: __('Child') },
          ]
        : [
            { value: 'spouse', label: __('Spouse') },
            { value: 'child', label: __('Child') },
            { value: 'parent', label: __('Parent') },
          ];

    // If there's no 'self' yet, or this is the 'self' person, include the 'self' option
    if (selfIndex === -1 || selfIndex === index) {
      return [{ value: 'self', label: __('Self') }, ...baseOptions];
    }

    // Otherwise, return options without 'self'
    return baseOptions;
  };

  useEffect(() => {
    state.topbar.title.set(__('Insured Persons Information'));
  }, [state.topbar.title]);

  return (
    <>
      <Box
        header={
          <>
            {__('Insured Persons Information')}
            <Button
              theme="simple"
              icon={<AddIcon />}
              onClick={handleAddInsuredPersonField}
              disabled={localInsuredPersons.length >= maxInsuredPerson}
            >
              {__('Add') +
                ' ' +
                localInsuredPersons.length +
                ' / ' +
                maxInsuredPerson}
            </Button>
          </>
        }
        footer={<FormFooter />}
      >
        {/* Display form-level error if any */}
        {formError && <div className={classes.errorSummary}>{formError}</div>}

        <div className={classes.formWrapper}>
          {
            // Map through the local insured persons array
            localInsuredPersons.map((insuredPerson, index) => (
              <div key={index} className={classes.insuredPersonSet}>
                <div className={classes.headerRow}>
                  <div className={classes.title}>
                    <div className={classes.titleText}>
                      {__('Insured Person') + ' ' + (index + 1)}
                      {localInsuredPersons.length > 1 && (
                        <div
                          className={classes.deleteButton}
                          onClick={() => handleDeleteInsuredPersonSet(index)}
                        >
                          <DeleteIcon />
                        </div>
                      )}
                    </div>
                    {insuredPerson.relationship === 'self' && (
                      <div className={classes.selfNote}>
                        {__('Information copied from policy holder')}
                      </div>
                    )}
                  </div>
                </div>
                {/* First field: relationship. The rest appear only after it is picked. */}
                <div className="row" style={{ marginBottom: '20px' }}>
                  <div className="col-md-4">
                    <SelectorBox
                      label={__('Relationship with policyholder')}
                      placeholder={__('Relationship with policyholder')}
                      options={getRelationshipOptions(index)}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(
                          index,
                          'relationship',
                          value
                        )
                      }
                      value={insuredPerson?.relationship || ''}
                      required
                      errorMessage={errors[index]?.relationship}
                    />
                  </div>
                  {insuredPerson?.relationship && (
                    <>
                  <div className="col-md-4">
                    <InputBox
                      label={__('Name of the insured person (English)')}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(index, 'name', value)
                      }
                      value={insuredPerson?.name || ''}
                      required
                      errorMessage={errors[index]?.name}
                      disabled={isFieldDisabled(insuredPerson, 'name')}
                    />
                  </div>
                  <div className="col-md-4">
                    <SelectorBox
                      label={__('Gender')}
                      options={[
                        { value: 'm', label: __('Male') },
                        { value: 'f', label: __('Female') },
                      ]}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(index, 'gender', value)
                      }
                      value={insuredPerson?.gender || ''}
                      required
                      errorMessage={errors[index]?.gender}
                      disabled={isFieldDisabled(insuredPerson, 'gender')}
                    />
                  </div>
                    </>
                  )}
                </div>
                {insuredPerson?.relationship && (
                  <div className="row">
                  <div className="col-md-4">
                    <SelectorBox
                      label={__('Identity Type')}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(index, 'idType', value)
                      }
                      value={insuredPerson?.idType || ''}
                      options={IDENTITY_TYPE}
                      required
                      errorMessage={errors[index]?.idType}
                      disabled={isFieldDisabled(insuredPerson, 'idType')}
                    />
                  </div>
                  <div className="col-md-4">
                    <InputBox
                      label={__('ID / Passport No.')}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(index, 'id', value)
                      }
                      value={insuredPerson?.id || ''}
                      required
                      errorMessage={errors[index]?.id}
                      disabled={isFieldDisabled(insuredPerson, 'id')}
                    />
                  </div>
                  <div className="col-md-4">
                    <DateSelector
                      label={__('Date of Birth')}
                      onChange={(value) =>
                        handleUpdateInsuredPersonField(
                          index,
                          'dateOfBirth',
                          value
                        )
                      }
                      minDate={
                        selectedInsurance?.settings?.maxAgeInMonths
                          ? moment()
                              .subtract(
                                selectedInsurance.settings.maxAgeInMonths,
                                'months'
                              )
                              .format(DATE_FORMAT)
                          : undefined
                      }
                      maxDate={
                        selectedInsurance?.settings?.minAgeInMonths
                          ? moment()
                              .subtract(
                                selectedInsurance.settings.minAgeInMonths,
                                'months'
                              )
                              .format(DATE_FORMAT)
                          : moment().format(DATE_FORMAT)
                      }
                      value={insuredPerson?.dateOfBirth || null}
                      required
                      errorMessage={errors[index]?.dateOfBirth}
                      disabled={isFieldDisabled(insuredPerson, 'dateOfBirth')}
                    />
                  </div>
                </div>
                )}
              </div>
            ))
          }
        </div>
      </Box>
    </>
  );
};

export default InsuranceFormStep2;

